import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLLABLE_CONTENT_TOLERANCE_PX = 1;

export const useScrollableContent = <T extends HTMLElement>(
  contentKey: string | number | boolean = ''
) => {
  const scrollContainerRef = useRef<T | null>(null);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const updateScrollableState = useCallback(() => {
    const element = scrollContainerRef.current;

    if (!element) {
      setHasScrollableContent(false);
      return;
    }

    setHasScrollableContent(
      element.scrollHeight - element.clientHeight >
        SCROLLABLE_CONTENT_TOLERANCE_PX
    );
  }, []);

  useEffect(() => {
    updateScrollableState();
  }, [contentKey, updateScrollableState]);

  useEffect(() => {
    const element = scrollContainerRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateScrollableState);
    resizeObserver.observe(element);

    Array.from(element.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    return () => {
      resizeObserver.disconnect();
    };
  }, [contentKey, updateScrollableState]);

  return {
    hasScrollableContent,
    scrollContainerRef,
    updateScrollableState,
  };
};
